import _Request from "@/service/request";
import { Urls } from "@/service/urls";
import i18n from "@/i18n";

export interface Branch {
  id: number;
  name: string;
  code: string;
  address?: string;
  description?: string;
  type: BranchType;
  status: BranchStatus;
  organizationId: number;
  organizationName?: string;
  parentBranchId?: number;
  parentBranchName?: string;
  childBranches?: Branch[];
  createdAt: string;
  updatedAt: string;
}

export enum BranchType {
  HEAD_OFFICE = 'HEAD_OFFICE',
  REGIONAL = 'REGIONAL',
  WAREHOUSE = 'WAREHOUSE',
  STORE = 'STORE'
}

export enum BranchStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED'
}

export interface BranchCreateRequest {
  name: string;
  code: string;
  address?: string;
  description?: string;
  type: BranchType;
  organizationId?: number;
  parentBranchId?: number;
}

export interface BranchUpdateRequest extends BranchCreateRequest {
  id: number;
  status: BranchStatus;
}

export class BranchService {
  private req = new _Request();
  private swal = require('sweetalert2');

  async createBranch(request: BranchCreateRequest): Promise<Branch> {
    try {
      const response = await this.req.post(Urls.BRANCH_CREATE, request);
      
      this.swal.fire({
        title: i18n.global.t('alert.successTitle'),
        text: i18n.global.t('branch.createSuccess'),
        icon: 'success',
        confirmButtonText: i18n.global.t('common.ok')
      });
      
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || i18n.global.t('branch.createFailed');
      
      this.swal.fire({
        title: i18n.global.t('alert.errorTitle'),
        text: errorMessage,
        icon: 'error',
        confirmButtonText: i18n.global.t('common.ok')
      });
      
      throw err;
    }
  }

  async updateBranch(id: number, request: BranchUpdateRequest): Promise<Branch> {
    try {
      const response = await this.req.put(`${Urls.BRANCH_UPDATE}/${id}`, request);
      
      this.swal.fire({
        title: i18n.global.t('alert.successTitle'),
        text: i18n.global.t('branch.updateSuccess'),
        icon: 'success',
        confirmButtonText: i18n.global.t('common.ok')
      });
      
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || i18n.global.t('branch.updateFailed');
      
      this.swal.fire({
        title: i18n.global.t('alert.errorTitle'),
        text: errorMessage,
        icon: 'error',
        confirmButtonText: i18n.global.t('common.ok')
      });
      
      throw err;
    }
  }

  async deactivateBranch(id: number): Promise<void> {
    try {
      await this.req.delete(`${Urls.BRANCH_DELETE}/${id}`);
      
      this.swal.fire({
        title: i18n.global.t('alert.successTitle'),
        text: i18n.global.t('branch.deactivateSuccess'),
        icon: 'success',
        confirmButtonText: i18n.global.t('common.ok')
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || i18n.global.t('branch.deactivateFailed');
      
      this.swal.fire({
        title: i18n.global.t('alert.errorTitle'),
        text: errorMessage,
        icon: 'error',
        confirmButtonText: i18n.global.t('common.ok')
      });
      
      throw err;
    }
  }

  async getBranchHierarchy(): Promise<Branch[]> {
    try {
      const response = await this.req.get(Urls.BRANCH_HIERARCHY);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch branch hierarchy:', error);
      throw error;
    }
  }

  async getAccessibleBranches(): Promise<Branch[]> {
    try {
      const response = await this.req.get(Urls.BRANCH_ACCESSIBLE);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch accessible branches:', error);
      throw error;
    }
  }

  async getBranchById(id: number): Promise<Branch> {
    try {
      const response = await this.req.get(`${Urls.BRANCH_GET}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch branch:', error);
      throw error;
    }
  }

  async getAccessibleBranchIds(): Promise<number[]> {
    try {
      const response = await this.req.get(Urls.BRANCH_ACCESSIBLE_IDS);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch accessible branch IDs:', error);
      throw error;
    }
  }

  // Helper methods
  getBranchTypeOptions() {
    return [
      { label: i18n.global.t('branch.types.headOffice'), value: BranchType.HEAD_OFFICE },
      { label: i18n.global.t('branch.types.regionalOffice'), value: BranchType.REGIONAL },
      { label: i18n.global.t('branch.types.warehouse'), value: BranchType.WAREHOUSE },
      { label: i18n.global.t('branch.types.store'), value: BranchType.STORE }
    ];
  }

  getBranchStatusOptions() {
    return [
      { label: i18n.global.t('branch.statuses.active'), value: BranchStatus.ACTIVE },
      { label: i18n.global.t('branch.statuses.inactive'), value: BranchStatus.INACTIVE },
      { label: i18n.global.t('branch.statuses.suspended'), value: BranchStatus.SUSPENDED }
    ];
  }

  formatBranchDisplay(branch: Branch): string {
    return `${branch.name} (${branch.code})`;
  }

  // Get branches for selection (flat list)
  flattenBranches(branches: Branch[]): Branch[] {
    const flattened: Branch[] = [];
    
    const flatten = (branchList: Branch[], level = 0) => {
      branchList.forEach(branch => {
        flattened.push({ ...branch, level } as any);
        if (branch.childBranches && branch.childBranches.length > 0) {
          flatten(branch.childBranches, level + 1);
        }
      });
    };
    
    flatten(branches);
    return flattened;
  }
}
